import type { TripItineraryData, TripItineraryItem, TripDay, TripStop } from 'tripPlanner/api/types'
import type { DrawerHotelBookedRoom } from 'tripPlanner/utils/types'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { getDayLabel, formatStopDateRange } from './chatDates'
import { isHotelSavedData } from './savedItemDataGuards'

// offerId is a generic identifier: hotels store it as savedItemData.offerId,
// experiences store it as savedItemData.experienceId under a different source type.
function itemMatchesOffer(item: TripItineraryItem, offerId: string): boolean {
  if (!item.savedItemData) return false
  const { savedItemData } = item
  if (isHotelSavedData(savedItemData)) return savedItemData.offerId === offerId
  if (savedItemData.source === 'offer_experience') return savedItemData.experienceId === offerId
  return false
}

export function findOfferItemInItinerary(
  offerId: string,
  itineraryData: TripItineraryData,
): TripItineraryItem | undefined {
  const allItems = itineraryData.stops.flatMap(stop => [
    ...stop.days.flatMap(day => day.items),
    ...(stop.itemsWithoutDay ?? []),
  ])
  return allItems.find(item => itemMatchesOffer(item, offerId))
}

export function isOfferBookedInItinerary(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): boolean {
  if (!offerId || !itineraryData?.stops) return false
  const item = findOfferItemInItinerary(offerId, itineraryData)
  return !!item?.isBooked
}

export function getOfferOrderId(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): string | undefined {
  if (!offerId || !itineraryData?.stops) return undefined
  const item = findOfferItemInItinerary(offerId, itineraryData)
  return item?.confirmationCode
}

export function getOfferBookedRooms(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): Array<DrawerHotelBookedRoom> | undefined {
  if (!offerId || !itineraryData?.stops) return undefined
  const item = findOfferItemInItinerary(offerId, itineraryData)
  if (!item?.isBooked || !item.confirmationCode || !item.savedItemData) return undefined

  const { savedItemData } = item
  if (!isHotelSavedData(savedItemData) || !savedItemData.bookedRooms?.length) return undefined

  return savedItemData.bookedRooms.map((room) => ({
    roomTypeName: room.roomTypeName,
    packageName: room.packageName,
    roomImageIds: room.roomImageIds,
    checkIn: savedItemData.checkIn ?? '',
    checkOut: savedItemData.checkOut ?? '',
    orderId: item.confirmationCode!,
    bookingNumber: room.bookingNumber,
  }))
}

const UNASSIGNED = 'unassigned' as const

function findPlaceLocationInItinerary(
  placeId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): { stop: TripItineraryData['stops'][number]; day: TripDay | undefined } | undefined {
  if (!placeId || !itineraryData?.stops) return undefined

  for (const stop of itineraryData.stops) {
    const matchingDay = stop.days.find(day =>
      day.items.some(item => item.itemPlaceDetail?.placeId === placeId),
    )
    if (matchingDay) return { stop, day: matchingDay }

    if (stop.itemsWithoutDay?.some(item => item.itemPlaceDetail?.placeId === placeId)) {
      return { stop, day: undefined }
    }
  }

  return undefined
}

export function findPlaceDayInItinerary(
  placeId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): TripDay | typeof UNASSIGNED | undefined {
  const result = findPlaceLocationInItinerary(placeId, itineraryData)
  if (!result) return undefined
  return result.day ?? UNASSIGNED
}

export function isPlaceInItinerary(
  placeId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): boolean {
  return findPlaceDayInItinerary(placeId, itineraryData) !== undefined
}

export function getItineraryDayLabel(
  placeId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): string | undefined {
  const result = findPlaceDayInItinerary(placeId, itineraryData)
  if (!result) return undefined
  if (result === UNASSIGNED) {
    const intl = getCurrentIntl()
    return intl.formatMessage({
      id: 'MgK5ISSCJKWklR8eg12e',
      defaultMessage: 'To be planned',
      description: 'Label for unassigned trip itinerary items that have no scheduled day',
    })
  }
  return getDayLabel(result.dayNumber, result.date)
}

export function findPlaceStopInItinerary(
  placeId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): { stop: TripItineraryData['stops'][number]; dayNumber: number | undefined; dayDate: string | undefined } | undefined {
  const result = findPlaceLocationInItinerary(placeId, itineraryData)
  if (!result) return undefined
  return { stop: result.stop, dayNumber: result.day?.dayNumber, dayDate: result.day?.date }
}

interface OfferDayRange {
  label: string
  type: 'days' | 'to-be-planned'
  firstDayNumber?: number
  nightsCount?: number
}

export function getOfferItineraryDayRange(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): OfferDayRange | undefined {
  if (!offerId || !itineraryData?.stops) return undefined

  const hasOffer = (item: TripItineraryItem) => itemMatchesOffer(item, offerId)

  const matchingDays = itineraryData.stops
    .flatMap(stop => stop.days)
    .filter(day => day.items.some(hasOffer))
    .sort((a, b) => a.dayNumber - b.dayNumber)

  if (matchingDays.length > 0) {
    const first = matchingDays[0]
    const last = matchingDays[matchingDays.length - 1]

    let label: string
    if (first.date && last.date) {
      label = first.date === last.date
        ? getDayLabel(first.dayNumber, first.date)
        : formatStopDateRange(first.date, last.date)
    } else {
      const intl = getCurrentIntl()
      label = first.dayNumber === last.dayNumber
        ? intl.formatMessage({
            id: 'u1wB26lOYLMgkxrwovRa',
            defaultMessage: 'Day {dayNumber}',
            description: 'Single day label for offer itinerary placement in trip planner',
          }, { dayNumber: first.dayNumber })
        : intl.formatMessage({
            id: 's1UGPnSR6WtdZhdKm6EX',
            defaultMessage: 'Day {firstDay}-{lastDay}',
            description: 'Day range label for offer itinerary placement in trip planner',
          }, { firstDay: first.dayNumber, lastDay: last.dayNumber })
    }

    return { label, type: 'days', firstDayNumber: first.dayNumber, nightsCount: Math.max(1, last.dayNumber - first.dayNumber) }
  }

  const isUnassigned = itineraryData.stops
    .some(stop => stop.itemsWithoutDay?.some(hasOffer))

  if (isUnassigned) {
    const intl = getCurrentIntl()
    return {
      label: intl.formatMessage({
        id: 'nWHuKJnHZR1G5XFSRZXT',
        defaultMessage: 'To be planned',
        description: 'Label for unassigned trip itinerary offer items that have no scheduled day',
      }),
      type: 'to-be-planned',
    }
  }
  return undefined
}

export function findOfferStopInItinerary(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): TripStop | undefined {
  if (!offerId || !itineraryData?.stops) return undefined

  const hasOffer = (item: TripItineraryItem) => itemMatchesOffer(item, offerId)

  return itineraryData.stops.find(stop =>
    stop.days.some(day => day.items.some(hasOffer)) ||
    stop.itemsWithoutDay?.some(hasOffer),
  )
}

export function findOfferStopDetails(
  offerId: string | undefined,
  itineraryData: TripItineraryData | undefined,
): { stop: TripStop; dayNumber: number | undefined; dayDate: string | undefined } | undefined {
  if (!offerId || !itineraryData?.stops) return undefined

  const hasOffer = (item: TripItineraryItem) => itemMatchesOffer(item, offerId)

  for (const stop of itineraryData.stops) {
    const matchingDay = stop.days.find(day => day.items.some(hasOffer))
    if (matchingDay) return { stop, dayNumber: matchingDay.dayNumber, dayDate: matchingDay.date }

    if (stop.itemsWithoutDay?.some(hasOffer)) {
      return { stop, dayNumber: undefined, dayDate: undefined }
    }
  }

  return undefined
}

export function getStopTotalNights(stop: TripStop): number {
  return stop.days.length
}

export function doesStayExceedTripDates(
  checkInDayNumber: number,
  nights: number,
  stop: TripStop,
): { exceeds: boolean; extraDays: number } {
  const totalDays = getStopTotalNights(stop)
  const checkoutDay = checkInDayNumber + nights
  const extraDays = Math.max(0, checkoutDay - totalDays)
  return { exceeds: extraDays > 0, extraDays }
}

export interface Coordinates {
  lat: number
  lng: number
}

const EARTH_RADIUS_KM = 6371

function haversineDistanceKm(a: Coordinates, b: Coordinates): number {
  const toRad = (deg: number) => deg * (Math.PI / 180)
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const sinLat = Math.sin(dLat / 2)
  const sinLng = Math.sin(dLng / 2)
  const h = sinLat * sinLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h))
}

const DEFAULT_MAX_DISTANCE_KM = 50

export function findNearestStop(
  coordinates: Coordinates | undefined,
  stops: ReadonlyArray<TripStop>,
  maxDistanceKm: number = DEFAULT_MAX_DISTANCE_KM,
): TripStop | undefined {
  if (!coordinates || stops.length === 0) return undefined

  let nearest: TripStop | undefined
  let nearestDistance = Infinity

  for (const stop of stops) {
    const stopCoords = stop.stopPlaceDetail
    if (!stopCoords) continue

    const distance = haversineDistanceKm(coordinates, stopCoords)
    if (distance <= maxDistanceKm && distance < nearestDistance) {
      nearest = stop
      nearestDistance = distance
    }
  }

  return nearest
}
